/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';

import { Button, Form, Grid, Icon, Modal } from '@mairistem/composants';
import { SectionHeader } from '@mairistem/administres-utils';
import { request } from '@mairistem/tools';

import { API_DATE_FORMAT } from '@/constants';

import {
  ChampProfession,
  ChampsEtatMatrimonialAnterieur,
} from '@/components/Common';
import {
  buildEtatAnterieurUpdatePayload,
  buildIndividuPutPayload,
} from '@/utils';

interface ModalConjointDiversProps {
  /** Endpoint générique des individus d'acte (`PUT {urlIndividus}/{id}`). */
  urlIndividus?: string;
  open: boolean;
  /** Identifiant de l'ACTE : construit l'URL de `service.update`. */
  identifiant?: number;
  /** Le conjoint : porte la profession. */
  individu?: any;
  /** L'acte : porte la situation antérieure et l'ex-conjoint. */
  acte?: any;
  /** 1 ou 2 : quel conjoint, donc quel préfixe de colonnes. */
  numeroConjoint: 1 | 2;
  service?: { update: (data: Record<string, any>) => Promise<any> };
  onClose: () => void;
  onSaved: (individu: any) => void;
  /** Rafraîchit l'acte après l'écriture de sa section antérieure. */
  onActeSaved?: (acte: any) => void;
}

/**
 * Sections « DIVERS » et « ETAT MATRIMONIAL ANTÉRIEUR » de l'onglet d'un
 * conjoint — le second des deux groupes, et le seul à chevaucher deux tables :
 *
 * - la profession est sur l'individu → `PUT /acte-individus/{id}` ;
 * - la situation antérieure et l'ex-conjoint sont sur l'acte →
 *   `PUT /numerisation-actes/mariage/{id}`.
 *
 * ⚠️ On écrit l'INDIVIDU d'abord, puis l'ACTE dans son `.then` — jamais en
 * parallèle (cf. `CLAUDE.md`, `request` n'est pas une `Promise`). L'ordre rend
 * aussi l'échec lisible : le premier appel a abouti, l'erreur est explicite.
 *
 * Décalque de `ModalDefuntDivers`, aux colonnes et au référentiel près : le
 * mariage n'a pas de sexe d'ex-conjoint et sa liste de situations lui est propre.
 */
const ModalConjointDivers = (props: ModalConjointDiversProps) => {
  const {
    open,
    identifiant,
    urlIndividus,
    individu,
    acte,
    numeroConjoint,
    service,
    onClose,
    onSaved,
    onActeSaved,
  } = props;

  const prefixe = `conjoint${numeroConjoint}`;

  const [data, setData] = React.useState<Record<string, any>>({});
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    setData({
      profession: individu?.profession,
      /*
       * Le contrôleur expose le code BRUT (`am_ex_ematantc`), pas le vocabulaire
       * du Cases : c'est bien la valeur du dropdown. Et la date est unifiée —
       * le back sait vers quelle colonne l'aiguiller.
       */
      etatMatrimonialAnterieureCode:
        acte?.[`${prefixe}EtatMatrimonialAnterieureCode`],
      dateEtatMatrimonial: acte?.[`${prefixe}DateEtatMatrimonial`],
      nomExConjoint: acte?.[`${prefixe}NomExConjoint`],
      prenomExConjoint: acte?.[`${prefixe}PrenomExConjoint`],
    });
  }, [open, individu, acte, prefixe]);

  const handleChange = React.useCallback(
    (
      _event: React.SyntheticEvent | null,
      payload: { name: string; value: unknown },
    ) => {
      const { name, value } = payload;
      setData((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  /** Enregistre la section antérieure sur l'acte, et referme. */
  const enregistrerActe = React.useCallback(() => {
    const payload = buildEtatAnterieurUpdatePayload(
      identifiant as number,
      numeroConjoint,
      data,
    );

    const enregistrement = service?.update(payload);

    // Pas de service : l'individu est déjà écrit, on referme sans mentir.
    if (!enregistrement) {
      setSaving(false);
      onClose();
      return;
    }

    enregistrement
      .then(() => {
        onActeSaved?.({ ...acte, ...payload.acte });
        onClose();
      })
      .catch(() => {
        toast.error(
          "Une erreur est survenue lors de l'enregistrement de la situation antérieure",
        );
      })
      .finally(() => {
        setSaving(false);
      });
  }, [service, identifiant, numeroConjoint, acte, data, onActeSaved, onClose]);

  const handleSave = React.useCallback(() => {
    setSaving(true);

    // Rien à écrire sur l'individu : on va droit à l'acte.
    if (!urlIndividus || _.isNil(individu?.identifiant)) {
      enregistrerActe();
      return;
    }

    const corps = buildIndividuPutPayload(individu, {
      profession: data?.profession,
    });

    request
      .put(`${urlIndividus}/${individu.identifiant}`, corps)
      .then(() => {
        onSaved({ ...individu, ...corps });
        enregistrerActe();
      })
      .catch(() => {
        toast.error(
          "Une erreur est survenue lors de l'enregistrement de la profession",
        );
        setSaving(false);
      });
  }, [urlIndividus, individu, data, onSaved, enregistrerActe]);

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header
        content={`Modifier la situation du ${numeroConjoint === 1 ? '1er' : '2eme'} conjoint`}
      />
      <Modal.Content>
        {/* @ts-ignore */}
        <Form>
          {/* @ts-ignore */}
          <SectionHeader title="Divers" />
          <Grid>
            <Grid.Row style={{ paddingLeft: '1em' }}>
              <Grid.Column computer={10} tablet={16} mobile={16}>
                <ChampProfession
                  name="profession"
                  value={data?.profession}
                  onChange={handleChange}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
          {/* @ts-ignore */}
          <SectionHeader title="État matrimonial antérieur" />
          <ChampsEtatMatrimonialAnterieur
            data={data}
            actif={open}
            formatDateSortie={API_DATE_FORMAT}
            onChange={handleChange}
          />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>
          <Icon name="close" />
          Annuler
        </Button>
        <Button
          positive
          loading={saving}
          disabled={saving}
          data-testid="conjoint-divers-enregistrer"
          onClick={handleSave}
        >
          <Icon name="check" />
          Enregistrer
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export const ModalConjoint1Divers = (props: Record<string, any>) => (
  <ModalConjointDivers {...(props as any)} numeroConjoint={1} />
);

export const ModalConjoint2Divers = (props: Record<string, any>) => (
  <ModalConjointDivers {...(props as any)} numeroConjoint={2} />
);

export default ModalConjointDivers;
